import fp from "lodash/fp";
import moment from "moment";

import {
	AchFeesFields,
	AriseFeesFields,
	InitialGuidId,
	ACHProcessorData as ach,
} from "globalConstants";
import {
	AmexTypeByValue,
	CreditDebitByValueTypeCart,
	formatE164,
	getStringWithoutHyphens,
	RefundPolicyByValue,
	TransactionFeeTypeByValue,
} from "helpers";
import { feesHardcode, getSelectedMonths } from "models";
import { TypeOfBusinessValues } from "types";
import {
	float6Normalizer,
	float2Normalizer,
	SSNNormalizer,
	taxIdNormalizer,
	SSNNormalizerAsterisk,
} from "validators";

import { mapAddressToApi, mapAddressFromApi } from "./geocodeAddress";

const parseGeocodedOwner = (
	owner: { [key: string]: any } | undefined | null,
	allowAsterisk: boolean = false,
) => {
	if (!owner) return;

	const address = mapAddressFromApi(owner?.address);

	return {
		id: owner?.id,
		firstName: owner?.firstName,
		lastName: owner?.lastName,
		ownership:
			owner?.ownership === 0 ? "" : float2Normalizer(owner?.ownership),
		dateOfBirth:
			owner?.dateOfBirth &&
			moment(owner?.dateOfBirth).format("MM/DD/YYYY"),
		titlePosition: owner?.titlePosition,
		homeMobilePhone: formatE164(owner?.homeMobilePhone),
		ssn: allowAsterisk
			? SSNNormalizerAsterisk(owner?.ssn)
			: SSNNormalizer(owner?.ssn),
		emailAddress: owner?.emailAddress,
		proceedWithoutTin: owner?.proceedWithoutTin,
		ip: owner?.ip,
		cityFromISP: owner?.cityFromISP,
		continent: owner?.continent,
		org: owner?.org,
		asn: owner?.asn,
		subdivision: owner?.subdivision,
		subdivision2: owner?.subdivision2,
		isAnIndividualWithSignificantResponsibility:
			owner?.isAnIndividualWithSignificantResponsibility,
		isAPoliticallyExposedPerson: owner?.isAPoliticallyExposedPerson,
		isByPassSignature: owner?.isByPassSignature,
		...address,
	};
};

export const parseOwner = (
	owner: { [key: string]: any } | undefined | null,
	allowAsterisk: boolean = false,
) => {
	if (!owner) return;

	return parseGeocodedOwner(owner, allowAsterisk);
};

export const principalInfoMapper = application => {
	if (fp.isEmpty(application)) return;

	const owners = fp.flow(
		fp.filter(fp.identity),
		fp.map(parseOwner),
	)([
		application.owner1,
		application.owner2,
		application.owner3,
		application.owner4,
	]);

	const signer = application.authorizedSigner
		? parseOwner(application.authorizedSigner)
		: {};

	return {
		ownerPrincipalSection: {
			owners,
			signer,
		},
		isPrimaryContactTheSame:
			application?.isPrimaryContactTheSame?.toString() || "false",
		email: application?.primaryContact?.email,
		firstName: application?.primaryContact?.firstName,
		lastName: application?.primaryContact?.lastName,
		phone: formatE164(application?.primaryContact?.phone),
		titlePosition: application?.primaryContact?.titlePosition,
	};
};

export const businessInfoMapper = application => {
	const businessAddress = mapAddressFromApi(application?.businessAddress);
	const legalAddress = mapAddressFromApi(application?.legalAddress);

	return (
		fp.isEmpty(application) || {
			businessAddress,
			legalAddress: {
				...legalAddress,
				isLegalAddressTheSame:
					application.isLegalAddressTheSame.toString(),
			},
			applicationId: application.id,
			dbaName: application.dbaName,
			website: application.website,
			businessPhone: formatE164(application.businessPhone),
			typeOfBusiness: TypeOfBusinessValues.getByValue(
				application.typeOfBusiness,
			)?.name,
			businessIfOther: application.businessIfOther,
			businessEstablishedDate:
				application.businessEstablishedDate &&
				moment(application.businessEstablishedDate).format(
					"MM/DD/YYYY",
				),
			legalName: application.legalName,
			fax: formatE164(application.fax),
			csPhone: formatE164(application.csPhone),
			businessEmail: application.businessEmail,
			isMerchantOptsOutOfReceivingFutureCommercialFromAmex:
				application.isMerchantOptsOutOfReceivingFutureCommercialFromAmex,
			taxId: taxIdNormalizer(application.taxId),
			swiped: float2Normalizer(application.swiped),
			keyed: float2Normalizer(application.keyed),
			eCommerce: float2Normalizer(application.eCommerce),
			refundPolicy: RefundPolicyByValue(application.refundPolicy, true),
			refundPolicyIfOther: application.refundPolicyIfOther,
			sellingOutsideOfUS: application.sellingOutsideOfUS.toString(),
			haveYouAcceptedCardsPreviously:
				application.haveYouAcceptedCardsPreviosly.toString(),
			whoWasPreviousProcessor: application.whoWasPreviousProcessor,
			hasAProcessorEverTerminatedYourMerchantAccount:
				application.hasAProcessorEverTerminatedYourMerchantAccount,
			ifSoWhoWasThatProcessor: application.ifSoWhoWasThatProcessor,
			minDaysToDelivery: application.minDaysToDelivery,
			avgDaysToDelivery: application.avgDaysToDelivery,
			maxDaysToDelivery: application.maxDaysToDelivery,
			recurringPayments: application.recurringPayments.toString(),
			doYouUseAThirdPartyForFulfillment:
				application.doYouUseAThirdPartyForFulfillment.toString(),
			whoDoYouUseAThirdPartyForFulfillment:
				application.whoDoYouUseAThirdPartyForFulfillment,
			doYouUseIntegratedSoftwareToProcessPayments:
				application.doYouUseIntegratedSoftwareToProcessPayments,
			creditAndDebitCardTransactionsAreSettled:
				CreditDebitByValueTypeCart(
					application.creditAndDebitCardTransactionsAreSettled,
					true,
				),
			creditDebitCardTransactionsAreSettledTypeIfOther:
				application.creditDebitCardTransactionsAreSettledTypeIfOther,
			routing: application.routing,
			bank: application.bank,
			account: application.account,
			accountName: application.accountName,

			siteInspectionBy: application.siteInspectionBy,
			noOfLocations: application.noOfLocations,
			merchantType: application.merchantType,
			previousAccountMIDNumber: application.previousAccountMIDNumber,
			monthlyVMCD: application?.monthlyVMCD,
			avgTicketVMCD: float2Normalizer(application?.avgTicketVMCD),
			highTicketVMCD: float2Normalizer(application?.highTicketVMCD),
			monthlyAMEX: float2Normalizer(application?.monthlyAMEX),
			avgTicketAMEX: float2Normalizer(application?.avgTicketAMEX),
			highTicketAMEX: float2Normalizer(application?.highTicketAMEX),
			seasonalChecked: application.seasonalChecked,
			seasonalMonths: getSelectedMonths(application.seasonalMonths),
			documentationMailingAddress:
				application.documentationMailingAddress,
			isPrintingPaperStatementEnabled:
				application.isPrintingPaperStatementEnabled ? "true" : "false",
			excludeCardTypes: application.excludeCardTypes?.reduce(
				(prev, type) => ({ ...prev, ...{ [type]: true } }),
				{},
			),
			isLegalAddressTheSame: undefined,
			// ACH fields
			[ach.MonthlyVolumeAch.name]:
				application?.achProcessorData?.[ach.MonthlyVolumeAch.beName],
			[ach.MonthlyVolumeAchPayouts.name]:
				application?.achProcessorData?.[
					ach.MonthlyVolumeAchPayouts.beName
				],
			[ach.HighTicketAch.name]:
				application?.achProcessorData?.[ach.HighTicketAch.beName],
			[ach.HighTicketAchPayouts.name]:
				application?.achProcessorData?.[
					ach.HighTicketAchPayouts.beName
				],
			[ach.TicketCountAch.name]:
				application?.achProcessorData?.[ach.TicketCountAch.beName],
			[ach.TicketCountAchPayouts.name]:
				application?.achProcessorData?.[
					ach.TicketCountAchPayouts.beName
				],
			[ach.StatementDescriptor.name]:
				application?.achProcessorData?.[ach.StatementDescriptor.beName],
			[ach.UseOfPaymentServiceDescription.name]:
				application?.achProcessorData?.[
					ach.UseOfPaymentServiceDescription.beName
				],
			[ach.IsApplyBankAccountData.name]:
				application?.achProcessorData?.[
					ach.IsApplyBankAccountData.beName
				].toString() || ach.IsApplyBankAccountData.Yes.value,
			[ach.BillingRouting.name]:
				application?.achProcessorData?.[ach.BillingRouting.beName],
			[ach.BillingBank.name]:
				application?.achProcessorData?.[ach.BillingBank.beName],
			[ach.BillingAccount.name]:
				application?.achProcessorData?.[ach.BillingAccount.beName],
			[ach.BillingAccountName.name]:
				application?.achProcessorData?.[ach.BillingAccountName.beName],
			[ach.CustomerType.name]:
				application?.achProcessorData?.[ach.CustomerType.beName] ||
				ach.CustomerType.Individuals.value,
			[ach.PaymentAuthorizationType.name]:
				application?.achProcessorData?.[
					ach.PaymentAuthorizationType.beName
				] || ach.PaymentAuthorizationType.SignedContract.value,
		}
	);
};

export const getStartedMapper = application => {
	return (
		fp.isEmpty(application) || {
			createdOf: application.createdOf,
			updatedOf: application.updatedOf,
			createdBy: application.createdBy,
			updatedBy: application.updatedBy,
			primaryAgent: application.primaryAgent,
			primaryAgentId: application.primaryAgent?.id.toString(),
			headquarters: application.headquarters,
			processorSponsorSection: {
				processor: application.processor && {
					id: application.processor?.id.toString(),
					name: application.processor?.name,
				},
				sponsor: {
					id: application.sponsor?.id.toString(),
				},
				achProcessor: application?.achProcessor,
			},
			residualsSection: {
				scheduleA: {
					id: application?.scheduleAId?.toString(),
				},
				splitProfile: {
					id: application?.splitProfileId?.toString(),
				},
			},
			mccCodeSection: {
				whatDoYouSell: application.whatDoYouSell,
				mccCode: application.mccCode,
				mccAliasId: application.mccAliasId,
				detailedExplanationWhatDoYouSell:
					application.detailedExplanationWhatDoYouSell,
			},
		}
	);
};

export const feesMapper = application => {
	if (fp.isEmpty(application)) return {};

	const miscFees = fp.isEmpty(application.miscFees)
		? [
				{
					value: "0.000000",
					percentage: "0.000000",
					type: "0",
					month: null,
					startDate: null,
					endDate: null,
					description: null,
					order: 1,
				},
		  ]
		: application.miscFees?.map(mf => {
				return {
					...mf,
					value: float6Normalizer(mf.value),
					percentage: float6Normalizer(mf.percentage),
					type: mf.type?.toString(),
					startDate: mf.startDate
						? moment(mf.startDate).format("MM/DD/YYYY")
						: null,
					endDate: mf.endDate
						? moment(mf.endDate).format("MM/DD/YYYY")
						: null,
				};
		  });

	return {
		feeProgram: TransactionFeeTypeByValue(application.feeProgram, true),
		feeTemplate: application.feeTemplate?.name,
		passCardBrandFees: application.passCardBrandFees.toString(),

		//interchange plus
		interchangePlusVMCD: float6Normalizer(application.interchangePlusVMCD),
		interchangePlusOfflineDebit: float6Normalizer(
			application.interchangePlusOfflineDebit,
		),
		interchangePlusAmex: float6Normalizer(application.interchangePlusAmex),

		commercialCardInterchangeServiceFee: float6Normalizer(
			application.commercialCardInterchangeServiceFee,
		),
		interchangePlusRevenueType:
			application.interchangePlusRevenueType.toString(),

		//flat
		flatRate: float6Normalizer(application.flatRate),
		flatRateIsRiseFree: application.flatRateIsRiseFree.toString(),

		//three tier
		threeTierQualVMCD: float6Normalizer(application.threeTierQualVMCD),
		threeTierMidQualVMCD: float6Normalizer(
			application.threeTierMidQualVMCD,
		),
		threeTierNonQualVMCD: float6Normalizer(
			application.threeTierNonQualVMCD,
		),
		threeTierNonQualAmex: float6Normalizer(
			application.threeTierNonQualAmex,
		),
		threeTierNonQualOfflineDebit: float6Normalizer(
			application.threeTierNonQualOfflineDebit,
		),
		threeTierMidQualAmex: float6Normalizer(
			application.threeTierMidQualAmex,
		),
		threeTierMidQualOfflineDebit: float6Normalizer(
			application.threeTierMidQualOfflineDebit,
		),
		threeTierQualOfflineDebit: float6Normalizer(
			application.threeTierQualOfflineDebit,
		),
		threeTierQualAmex: float6Normalizer(application.threeTierQualAmex),
		isPassThroughInterchange: application.isPassThroughInterchange,

		//err
		errNonQualVMCD: float6Normalizer(application.errNonQualVMCD),
		errNonQualAmex: float6Normalizer(application.errNonQualAmex),
		errNonQualOfflineDebit: float6Normalizer(
			application.errNonQualOfflineDebit,
		),
		errQualOfflineDebit: float6Normalizer(application.errQualOfflineDebit),
		errQualAmex: float6Normalizer(application.errQualAmex),
		errQualVMCD: float6Normalizer(application.errQualVMCD),

		//dual flat rate
		dualFlatRateCardPresent: float6Normalizer(
			application.dualFlatRateCardPresent,
		),
		dualFlatRateCardPresentAuthFee: float6Normalizer(
			application.dualFlatRateCardPresentAuthFee,
		),
		dualFlatRateCardNotPresent: float6Normalizer(
			application.dualFlatRateCardNotPresent,
		),
		dualFlatRateCardNotPresentAuthFee: float6Normalizer(
			application.dualFlatRateCardNotPresentAuthFee,
		),

		//additional
		authorizationFee: float6Normalizer(application.authorizationFee),
		salesTransactionFee: float6Normalizer(application.salesTransactionFee),
		avs: float6Normalizer(application.avs),
		aru: float6Normalizer(application.aru),
		batchSettlement: float6Normalizer(application.batchSettlement),
		additionalFeeOtherItemFee: float6Normalizer(
			application.additionalFeeOtherItemFee,
		),
		riseFreeTransFee: float6Normalizer(application.riseFreeTransFee),
		voiceAuth: float6Normalizer(application.voiceAuth),
		voiceAVS: float6Normalizer(application.voiceAVS),
		otherVolume: float6Normalizer(application.otherVolume),
		returnTransactionFee: float6Normalizer(
			application.returnTransactionFee,
		),
		retrievalFee: float6Normalizer(application.retrievalFee),

		applicationFee: float6Normalizer(application.applicationFee),
		earlyTerminationFee: float6Normalizer(application.earlyTerminationFee),
		annualFee: float6Normalizer(application.annualFee),
		monthAnnualFeeBilled: application.monthAnnualFeeBilled,

		gatewayPerAuth: float6Normalizer(application.gatewayPerAuth),
		gatewayDepositFee: float6Normalizer(application.gatewayDepositFee),
		gatewayAdjustmentsFee: float6Normalizer(
			application.gatewayAdjustmentsFee,
		),

		ebtAccountNumber: application.ebtAccountNumber,
		ebtCashPerItemFee: float6Normalizer(application.ebtCashPerItemFee),
		ebtFoodPerItemFee: float6Normalizer(application.ebtFoodPerItemFee),

		miscFees: miscFees,

		isMonthEnd: application.isMonthEnd.toString(),
		discountType: application.discountType.toString(),

		//monthly
		customerServiceFee: float6Normalizer(application.customerServiceFee),
		regulatoryProductFee: float6Normalizer(
			application.regulatoryProductFee,
		),
		wirelessFee: float6Normalizer(application.wirelessFee),
		equipmentFee: float6Normalizer(application.equipmentFee),
		onlineReportingFee: float6Normalizer(application.onlineReportingFee),
		softwareFee: float6Normalizer(application.softwareFee),
		statementFee: float6Normalizer(application.statementFee),
		riseFreeProgramFee: float6Normalizer(application.riseFreeProgramFee),
		pciComplianceFee: float6Normalizer(application.pciComplianceFee),
		pciNonCompilance: float6Normalizer(application.pciNonCompilance),
		toBeBilled: application.toBeBilled.toString(),
		monthPCIAnnualFeeCharged:
			application.monthPCIAnnualFeeCharged.toString(),

		monthlyMinimumFee: float6Normalizer(application.monthlyMinimumFee),
		tinInvalidFee: float6Normalizer(application.tinInvalidFee),
		achRejectFee: float6Normalizer(application.achRejectFee),
		merchantMonitoringFee: float6Normalizer(
			feesHardcode.merchantMonitoringFee,
		),
		chargebackFee: float6Normalizer(application.chargebackFee),
		inactiveFee: float6Normalizer(application.inactiveFee),

		isPinDebitEnabled: application.isPinDebitEnabled,
		pinDebitSponsorship: application.pinDebitSponsorship,
		monthlyAccessFee: float6Normalizer(application.monthlyAccessFee),
		otherVolumeFee: float6Normalizer(application.otherVolumeFee),
		authorizationFeeOtherItemFee: float6Normalizer(
			application.authorizationFeeOtherItemFee,
		),

		voyagerQual: float6Normalizer(application.voyagerQual),
		voyagerOtherItemRate: float6Normalizer(
			application.voyagerOtherItemRate,
		),
		wexQual: float6Normalizer(application.wexQual),
		wexOtherItemRate: float6Normalizer(application.wexOtherItemRate),

		amexType: AmexTypeByValue(application.amexType, true),
		amexESANumber: application.amexESANumber,
		amexOtherItemFee: float6Normalizer(application.amexOtherItemFee),
		amexOtherVolumeFee: float6Normalizer(application.amexOtherVolumeFee),
		isActiveAmexOptBlueOrEsaSection: application.amexType !== 0,

		isCardNetworkFeesAvailable: application?.isCardNetworkFeesAvailable,

		// ACH Fees
		[AchFeesFields.SetupFee.name]: float6Normalizer(
			application?.[AchFeesFields.SetupFee.beName],
		),
		[AchFeesFields.MonthlyFee.name]: float6Normalizer(
			application?.[AchFeesFields.MonthlyFee.beName],
		),
		[AchFeesFields.TransactionFee.name]: float6Normalizer(
			application?.[AchFeesFields.TransactionFee.beName],
		),
		[AchFeesFields.OriginationDiscountFee.name]: float6Normalizer(
			application?.[AchFeesFields.OriginationDiscountFee.beName],
		),
		[AchFeesFields.OriginationLargeTransactionDiscountFee.name]:
			float6Normalizer(
				application?.[
					AchFeesFields.OriginationLargeTransactionDiscountFee.beName
				],
			),
		[AchFeesFields.StandardReturnFee.name]: float6Normalizer(
			application?.[AchFeesFields.StandardReturnFee.beName],
		),
		[AchFeesFields.NoticeOfChangeReturnFee.name]: float6Normalizer(
			application?.[AchFeesFields.NoticeOfChangeReturnFee.beName],
		),
		[AchFeesFields.ChargebackReturnFee.name]: float6Normalizer(
			application?.[AchFeesFields.ChargebackReturnFee.beName],
		),
		[AchFeesFields.AccountVerificationFee.name]: float6Normalizer(
			application?.[AchFeesFields.AccountVerificationFee.beName],
		),
		[AchFeesFields.AccountVerificationPlusFee.name]: float6Normalizer(
			application?.[AchFeesFields.AccountVerificationPlusFee.beName],
		),

		// Arise Fees
		[AriseFeesFields.PlatformSetupFee.name]: float6Normalizer(
			application?.[AriseFeesFields.PlatformSetupFee.beName],
		),
		[AriseFeesFields.PlatformMonthlyFee.name]: float6Normalizer(
			application?.[AriseFeesFields.PlatformMonthlyFee.beName],
		),
		[AriseFeesFields.CustomerVaultMonthlyFee.name]: float6Normalizer(
			application?.[AriseFeesFields.CustomerVaultMonthlyFee.beName],
		),
		[AriseFeesFields.EnhancedDataTransactionFee.name]: float6Normalizer(
			application?.[AriseFeesFields.EnhancedDataTransactionFee.beName],
		),
		[AriseFeesFields.CardTransactionAuthorizationFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.CardTransactionAuthorizationFee.beName
				],
			),
		[AriseFeesFields.ECommercePluginSetupFee.name]: float6Normalizer(
			application?.[AriseFeesFields.ECommercePluginSetupFee.beName],
		),
		[AriseFeesFields.ECommercePluginMonthlyFee.name]: float6Normalizer(
			application?.[AriseFeesFields.ECommercePluginMonthlyFee.beName],
		),
		[AriseFeesFields.EMVDeviceSetupFee.name]: float6Normalizer(
			application?.[AriseFeesFields.EMVDeviceSetupFee.beName],
		),
		[AriseFeesFields.AdditionalEmvDeviceMonthlyFee.name]: float6Normalizer(
			application?.[AriseFeesFields.AdditionalEmvDeviceMonthlyFee.beName],
		),
		[AriseFeesFields.EnhancedDataSetupFee.name]: float6Normalizer(
			application?.[AriseFeesFields.EnhancedDataSetupFee.beName],
		),
		[AriseFeesFields.CustomerVaultSetupFee.name]: float6Normalizer(
			application?.[AriseFeesFields.CustomerVaultSetupFee.beName],
		),
		[AriseFeesFields.CustomerVaultAutomaticUpdateFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.CustomerVaultAutomaticUpdateFee.beName
				],
			),
		[AriseFeesFields.MobileInvoicingPaymentsSetupFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.MobileInvoicingPaymentsSetupFee.beName
				],
			),
		[AriseFeesFields.MobileInvoicingPaymentsMonthlyFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.MobileInvoicingPaymentsMonthlyFee.beName
				],
			),
		[AriseFeesFields.MobileInvoicingPaymentsMessageFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.MobileInvoicingPaymentsMessageFee.beName
				],
			),

		// Card Network Tokenization Service Fees
		[AriseFeesFields.CardNetworkTokenizationSetupFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.CardNetworkTokenizationSetupFee.beName
				],
			),
		[AriseFeesFields.CardNetworkTokenizationMonthlyFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.CardNetworkTokenizationMonthlyFee.beName
				],
			),
		[AriseFeesFields.CardNetworkPerTokenizationEventFee.name]:
			float6Normalizer(
				application?.[
					AriseFeesFields.CardNetworkPerTokenizationEventFee.beName
				],
			),
	};
};

export const additionalServicesMapper = application => {
	return (
		fp.isEmpty(application) || {
			ach: application.ach,
			cashAdvance: application.cashAdvance,
			multiCurrency: application.multiCurrency,
			businessConnections: application.businessConnections,
			checkServices: application.checkServices,
			giftLoyalty: application.giftLoyalty,
			nailSoftGiftLoyalty: application.nailSoftGiftLoyalty,
			existingProvider: application.existingProvider,
			existingAcctNumber: application.existingAcctNumber,
			// Bundles
			bundles: application?.bundles
				? {
						bundle_1_name: application?.bundle1 || null,
						bundle_1_price: application?.priceBundle1 || null,
						bundle_2_name: application?.bundle2 || null,
						bundle_2_price: application?.priceBundle2 || null,
						notes: application?.noteBundle || null,
				  }
				: null,
		}
	);
};

export const signApplicationMapper = application => {
	return {
		selectedPDFTemplateType: application.selectedPDFTemplateType,
	};
};

export const deleteEmptyValues = application => {
	function isEmpty(value) {
		return value === "" || value === null;
	}

	for (const key in application) {
		if (typeof application[key] === "object") {
			for (const childKey in application[key]) {
				if (isEmpty(application[key][childKey]))
					delete application[key][childKey];
			}
		}
		if (isEmpty(application[key])) {
			delete application[key];
		}
	}
};

export const appStatuses = {
	UNSIGNED_REQUESTING_SIGN: 1,
	UNSIGNED_SIGN_REQUESTED: 2,
	UNSIGNED_SIGN_FAILED: 3,
	UNDERWRITING_UNASSIGNED: 4,
	UNDERWRITING_IN_PROGRESS: 5,
	UNDERWRITING_REJECTED: 10,
	BOARDING_UNASSIGNED: 11,
	BOARDING_IN_PROGRESS: 12,
	BOARDING_REJECTED: 14,
	UNSIGNED_CREATED_BY_MERCHANT: 20,
	REMOVED: 21,
	UNSIGNED_PENDING_COMPLETION: 22,
	EQUIPMENT_UNASSIGNED: 24,
	EQUIPMENT_IN_PROGRESS: 25,
	INSTALLATION_UNASSIGNED: 26,
	INSTALLATION_IN_PROGRESS: 27,
	COMPLETED: 28,
	UPLOADED: 29,
	UNSIGNED_PRE_APP_LINK_SENT: 30,
};

export function getAppStatuses(type) {
	let name = "";
	switch (type) {
		case appStatuses.UNSIGNED_REQUESTING_SIGN:
			name = "Submitted (In Progress)";
			break;
		case appStatuses.UNSIGNED_SIGN_REQUESTED:
			name = "Submitted (Waiting for Signature)";
			break;
		case appStatuses.UNSIGNED_SIGN_FAILED:
			name = "Submitted (Failed)";
			break;
		case appStatuses.UNDERWRITING_UNASSIGNED:
			name = "Underwriting (Unassigned)";
			break;
		case appStatuses.UNDERWRITING_IN_PROGRESS:
			name = "Underwriting (In review)";
			break;
		case appStatuses.UNDERWRITING_REJECTED:
			name = "Underwriting (Declined)";
			break;
		case appStatuses.BOARDING_UNASSIGNED:
			name = "Boarding (Unassigned)";
			break;
		case appStatuses.BOARDING_IN_PROGRESS:
			name = "Boarding (In Progress)";
			break;
		case appStatuses.BOARDING_REJECTED:
			name = "Boarding (Rejected)";
			break;
		case appStatuses.UNSIGNED_CREATED_BY_MERCHANT:
			name = "Draft";
			break;
		case appStatuses.REMOVED:
			name = "Removed";
			break;
		case appStatuses.UNSIGNED_PENDING_COMPLETION:
			name = "Pre-Application (Pending completion)";
			break;
		case appStatuses.UNSIGNED_PRE_APP_LINK_SENT:
			name = "Pre-Application (Link Sent)";
			break;
		case appStatuses.EQUIPMENT_UNASSIGNED:
			name = "TID & Equipment (Unassigned)";
			break;
		case appStatuses.EQUIPMENT_IN_PROGRESS:
			name = "TID & Equipment (In Progress)";
			break;
		case appStatuses.INSTALLATION_UNASSIGNED:
			name = "Installation (Unassigned)";
			break;
		case appStatuses.INSTALLATION_IN_PROGRESS:
			name = "Installation (In Progress)";
			break;
		case appStatuses.UPLOADED:
			name = "Signed MPA";
			break;
		case appStatuses.COMPLETED:
			name = "Completed";
			break;
		default:
			break;
	}
	return name;
}

export const mapOwnerToApi = owner => {
	const address = mapAddressToApi(owner);

	const id = owner?.id === InitialGuidId ? null : owner?.id;

	return {
		id,
		firstName: owner?.firstName,
		lastName: owner?.lastName,
		ownership: owner?.ownership,
		dateOfBirth: owner?.dateOfBirth,
		titlePosition: owner?.titlePosition,
		homeMobilePhone: formatE164(owner?.homeMobilePhone) || null,
		ssn: getStringWithoutHyphens(owner?.ssn),
		emailAddress: owner?.emailAddress,
		proceedWithoutTin: owner?.proceedWithoutTin,
		ip: owner?.ip,
		cityFromISP: owner?.cityFromISP,
		continent: owner?.continent,
		org: owner?.org,
		asn: owner?.asn,
		subdivision: owner?.subdivision,
		subdivision2: owner?.subdivision2,
		isAnIndividualWithSignificantResponsibility:
			owner?.isAnIndividualWithSignificantResponsibility,
		isAPoliticallyExposedPerson: owner?.isAPoliticallyExposedPerson,
		isByPassSignature: owner?.isByPassSignature,
		address,
	};
};
